import React, { useEffect, useRef } from "react";
import { useMessages } from "../context/MessagesContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  MessageCircle,
} from "lucide-react";

const GOOGLE_MAPS_API_KEY = process.env?.REACT_APP_GOOGLE_MAPS_API_KEY;
// Requerido por AdvancedMarkerElement; sin él se usa el marcador clásico
const GOOGLE_MAPS_MAP_ID = process.env?.REACT_APP_GOOGLE_MAPS_MAP_ID;

const GOOGLE_MAPS_CALLBACK = "__hidromedGoogleMapsReady";

// Promesa compartida: el script se inserta una sola vez aunque el componente se monte varias veces
let googleMapsPromise = null;

export function loadGoogleMapsScript(apiKey) {
  if (googleMapsPromise) return googleMapsPromise;

  googleMapsPromise = new Promise((resolve, reject) => {
    // Si ya está cargado, no lo vuelvas a cargar
    if (window.google?.maps?.importLibrary) return resolve();

    // Con loading=async el evento "load" no garantiza que la API esté lista; se usa el callback
    window[GOOGLE_MAPS_CALLBACK] = () => {
      delete window[GOOGLE_MAPS_CALLBACK];
      resolve();
    };

    const script = document.createElement("script");
    script.dataset.googleMaps = "true";
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&v=weekly&loading=async&callback=${GOOGLE_MAPS_CALLBACK}`;
    script.async = true;
    script.onerror = () => {
      googleMapsPromise = null;
      delete window[GOOGLE_MAPS_CALLBACK];
      script.remove();
      reject(new Error("Google Maps script failed to load"));
    };
    document.head.appendChild(script);
  });

  return googleMapsPromise;
}

export default function Contacto() {
  const labels = useMessages();

  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);

  useEffect(() => {
    const init = async () => {
      if (!GOOGLE_MAPS_API_KEY) {
        console.error(
          "Falta GOOGLE MAPS API KEY. Por favor, configúrala en las variables de entorno."
        );
        return;
      }

      try {
        await loadGoogleMapsScript(GOOGLE_MAPS_API_KEY);

        const { Map: GoogleMap } = await window.google.maps.importLibrary("maps");
        const { AdvancedMarkerElement, Marker } = await window.google.maps.importLibrary("marker");

        const hidromed = { lat: 32.5265873, lng: -117.0092127 };

        // Evita re-inicializar si ya existe
        if (!mapInstanceRef.current && mapRef.current) {
          mapInstanceRef.current = new GoogleMap(mapRef.current, {
            center: hidromed,
            zoom: 17,
            gestureHandling: "greedy", // ✅ zoom con scroll sin Ctrl
            zoomControl: true,
            streetViewControl: true,
            mapTypeControl: true,
            fullscreenControl: true,
            ...(GOOGLE_MAPS_MAP_ID && { mapId: GOOGLE_MAPS_MAP_ID }),
          });

          const MarkerClass = GOOGLE_MAPS_MAP_ID ? AdvancedMarkerElement : Marker;
          markerRef.current = new MarkerClass({
            position: hidromed,
            map: mapInstanceRef.current,
            title: "Hidromed Tijuana",
          });
        }
      } catch (e) {
        console.error(e);
      }
    };

    init();

    // Limpieza opcional (no elimina el script, solo referencias)
    return () => {
      markerRef.current = null;
      mapInstanceRef.current = null;
    };
  }, []);

  return (
    <section id="contacto" className="section-elegant scroll-mt-24">
      <div className="container mx-auto lg:px-8">
        {/* Header estilo "Quiénes Somos" */}
        <div className="text-center mb-12 pt-8">
          <h2 className="section-title">
            {labels["contact.title"]}
            </h2>
          <p className="body-copy mt-4 max-w-2xl mx-auto">
            {labels["contact.description"]}
          </p>
        </div>
      </div>

      {/* Contact Information */}
      <section className="py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="section-title mb-6">
              {labels["contact.types.title"]}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Phone */}
            <Card className="section-surface text-center bg-tarjetas border-0 hover:shadow-elevated transition-all duration-300 transform hover:-translate-y-2">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-green-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <Phone className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  {labels["contact.types.phone.1"]}
                </h3>
                <p className="text-gray-600 mb-4">{labels["contact.types.phone.2"]}</p>
                <div className="space-y-2">
                  <p className="font-semibold text-green-600">{labels["contact.types.phone.3"]}</p>
                  <p className="text-sm text-gray-500">{labels["contact.types.phone.4"]}</p>
                </div>
              </CardContent>
            </Card>

            {/* Email */}
            <Card className="section-surface text-center bg-tarjetas border-0 hover:shadow-elevated transition-all duration-300 transform hover:-translate-y-2">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-gradient-to-r from-cyan-500 to-cyan-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <Mail className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  {labels["contact.types.email.1"]}
                </h3>
                <p className="text-gray-600 mb-4">{labels["contact.types.email.2"]}</p>
                <div className="space-y-2">
                  <p className="font-semibold text-cyan-600">{labels["contact.types.email.3"]}</p>
                  <p className="text-sm text-gray-500">{labels["contact.types.email.4"]}</p>
                </div>
              </CardContent>
            </Card>

            {/* Horarios */}
            <Card className="section-surface text-center bg-tarjetas border-0 hover:shadow-elevated transition-all duration-300 transform hover:-translate-y-2">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <Clock className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  {labels["contact.hours.title"]}
                </h3>
                {[
                  { day: labels["contact.hours.days.1"], hours: labels["contact.hours.hours.1"] },
                  { day: labels["contact.hours.days.2"], hours: labels["contact.hours.hours.2"] },
                  { day: labels["contact.hours.days.3"], hours: labels["contact.hours.hours.3"] },
                ].map((schedule, index) => (
                  <div
                    key={index}
                    className="flex justify-between items-center py-3 border-b border-gray-100 last:border-0"
                  >
                    <span className="text-sm">{schedule.day}</span>
                    <span className="text-sm text-cyan-600">{schedule.hours}</span>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* WhatsApp */}
            <Card className="section-surface text-center bg-tarjetas border-0 hover:shadow-elevated transition-all duration-300 transform hover:-translate-y-2">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-gradient-to-r from-green-400 to-green-500 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <MessageCircle className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  {labels["contact.types.whatsapp.1"]}
                </h3>
                <p className="text-gray-600 mb-4">{labels["contact.types.whatsapp.2"]}</p>
                <div className="space-y-2">
                  <a
                    href="https://wa.me/5216641954918"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-4 rounded-lg transition duration-300"
                  >
                    {labels["contact.types.whatsapp.3"]}
                  </a>
                  <p className="text-sm text-gray-500">{labels["contact.types.whatsapp.4"]}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Hours & Location Details */}
      <section className="py-2 from-gray-50 to-cyan-50">
        <div className="w-full sm:px-6">
          <div className="w-full">

            {/* Location + Map */}
            <Card className="section-surface bg-tarjetas border-0">
              <CardHeader className="text-center pb-6">
                <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <MapPin className="w-8 h-8 text-white" />
                </div>
                <CardTitle className="text-2xl font-bold text-gray-900">
                  {labels["contact.location.title"]}
                </CardTitle>
              </CardHeader>

              <CardContent className="space-y-2">
                {[
                  {
                    name: labels["contact.location.address.1"],
                    address: labels["contact.location.address.2"],
                    details: labels["contact.location.address.3"],
                    city: labels["contact.location.address.4"],
                  },
                ].map((location, index) => (
                  <div key={index} className="p-4 from-purple-50 to-pink-50 rounded-xl">
                    <h4 className="font-bold text-gray-900 mb-2">{location.name}</h4>
                    <p className="text-gray-700 mb-1">{location.address}</p>
                    <p className="text-gray-600 mb-1">{location.details}</p>
                    <p className="text-gray-600 mb-3">{location.city}</p>
                  </div>
                ))}

                {/* ✅ Mapa */}
                <div className="rounded-2xl overflow-hidden shadow-lg">
                  <div
                    ref={mapRef}
                    className="w-full"
                    style={{ height: 360 }}
                  />
                </div>

                {!GOOGLE_MAPS_API_KEY && (
                  <p className="text-sm text-red-600">
                    Falta configurar la API Key de Google Maps en variables de entorno.
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </section>
  );
}

